import { useState } from "react";
import ExcelImport from './ExcelImport';
import { TrendingUp, TrendingDown, Info, Save, Plus, Trash2, Package, X } from "lucide-react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";

const CURRENCIES = [
  { code: "INR", symbol: "₹", label: "Indian Rupee" },
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "AED", symbol: "د.إ", label: "UAE Dirham" },
  { code: "SGD", symbol: "S$", label: "Singapore Dollar" },
  { code: "CAD", symbol: "C$", label: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar" },
];


// Full metrics library — 45+ metrics
const METRICS_LIBRARY = [
  // Growth
  { id: "mrr", label: "MRR", category: "Growth", unit: "₹", desc: "Monthly Recurring Revenue", inputKey: "mrr", computed: false, higher: true, defaultGood: 500000, defaultExc: 2000000 },
  { id: "arr", label: "ARR", category: "Growth", unit: "₹", desc: "Annual Recurring Revenue (MRR × 12)", computed: true, higher: true, defaultGood: 6000000, defaultExc: 24000000 },
  { id: "growth", label: "MoM Growth", category: "Growth", unit: "%", desc: "Month over month revenue growth", inputKey: "growth", computed: false, higher: true, defaultGood: 15, defaultExc: 25 },
  { id: "yoyGrowth", label: "YoY Growth", category: "Growth", unit: "%", desc: "Year over year growth (MoM × 12)", computed: true, higher: true, defaultGood: 100, defaultExc: 200 },
  { id: "nrr", label: "NRR", category: "Growth", unit: "%", desc: "Net Revenue Retention", inputKey: "nrr", computed: false, higher: true, defaultGood: 110, defaultExc: 130 },
  { id: "grr", label: "GRR", category: "Growth", unit: "%", desc: "Gross Revenue Retention (100 - Revenue Churn)", inputKey: "grr", computed: false, higher: true, defaultGood: 85, defaultExc: 95 },
  { id: "expansionRevenue", label: "Expansion Revenue", category: "Growth", unit: "%", desc: "% of revenue from expansion/upsell", inputKey: "expansionRevenue", computed: false, higher: true, defaultGood: 20, defaultExc: 40 },
  // Efficiency
  { id: "burnMultiple", label: "Burn Multiple", category: "Efficiency", unit: "x", desc: "Net burn / net new ARR. Lower is better.", computed: true, higher: false, defaultGood: 2, defaultExc: 1 },
  { id: "magicNumber", label: "Magic Number", category: "Efficiency", unit: "x", desc: "New ARR / Sales & Marketing spend", computed: true, higher: true, defaultGood: 1, defaultExc: 1.5 },
  { id: "arrPerEmployee", label: "ARR / Employee", category: "Efficiency", unit: "₹", desc: "ARR divided by headcount", computed: true, higher: true, defaultGood: 1500000, defaultExc: 3000000 },
  { id: "revenuePerEmployee", label: "Revenue / Employee", category: "Efficiency", unit: "₹", desc: "Monthly revenue per employee", computed: true, higher: true, defaultGood: 100000, defaultExc: 250000 },
  { id: "smRatio", label: "S&M Ratio", category: "Efficiency", unit: "%", desc: "Sales & Marketing as % of revenue", computed: true, higher: false, defaultGood: 30, defaultExc: 15 },
  { id: "rdRatio", label: "R&D Ratio", category: "Efficiency", unit: "%", desc: "R&D spend as % of revenue", inputKey: "rdSpend", computed: false, higher: false, defaultGood: 25, defaultExc: 15 },
  { id: "opexRatio", label: "Opex Ratio", category: "Efficiency", unit: "%", desc: "Operating expenses as % of revenue", computed: true, higher: false, defaultGood: 80, defaultExc: 60 },
  { id: "quickRatio", label: "Quick Ratio", category: "Efficiency", unit: "x", desc: "New + Expansion MRR / Churned MRR", computed: true, higher: true, defaultGood: 4, defaultExc: 8 },
  // Profitability
  { id: "grossMargin", label: "Gross Margin", category: "Profitability", unit: "%", desc: "Revenue minus COGS as % of revenue", inputKey: "grossMargin", computed: false, higher: true, defaultGood: 70, defaultExc: 80 },
  { id: "ruleOf40", label: "Rule of 40", category: "Profitability", unit: "%", desc: "Growth rate + gross margin − 100", computed: true, higher: true, defaultGood: 40, defaultExc: 60 },
  { id: "ebitdaMargin", label: "EBITDA Margin", category: "Profitability", unit: "%", desc: "EBITDA as % of revenue", inputKey: "ebitdaMargin", computed: false, higher: true, defaultGood: 0, defaultExc: 20 },
  { id: "operatingLeverage", label: "Operating Leverage", category: "Profitability", unit: "x", desc: "Revenue growth / opex growth", inputKey: "operatingLeverage", computed: false, higher: true, defaultGood: 1.5, defaultExc: 2 },
  { id: "grossProfit", label: "Gross Profit", category: "Profitability", unit: "₹", desc: "Revenue × Gross Margin", computed: true, higher: true, defaultGood: 500000, defaultExc: 2000000 },
  { id: "contributionMargin", label: "Contribution Margin", category: "Profitability", unit: "%", desc: "Revenue minus variable costs as %", inputKey: "contributionMargin", computed: false, higher: true, defaultGood: 50, defaultExc: 70 },
  // Unit Economics
  { id: "ltv", label: "LTV", category: "Unit Economics", unit: "₹", desc: "Customer Lifetime Value", inputKey: "ltv", computed: false, higher: true, defaultGood: 100000, defaultExc: 500000 },
  { id: "cac", label: "CAC", category: "Unit Economics", unit: "₹", desc: "Customer Acquisition Cost", inputKey: "cac", computed: false, higher: false, defaultGood: 10000, defaultExc: 5000 },
  { id: "ltvCac", label: "LTV:CAC", category: "Unit Economics", unit: "x", desc: "Customer LTV divided by CAC", computed: true, higher: true, defaultGood: 3, defaultExc: 5 },
  { id: "cacPayback", label: "CAC Payback", category: "Unit Economics", unit: "mo", desc: "Months to recover CAC from gross margin", computed: true, higher: false, defaultGood: 18, defaultExc: 12 },
  { id: "churn", label: "Logo Churn", category: "Unit Economics", unit: "%", desc: "% of customers lost per month", inputKey: "churn", computed: false, higher: false, defaultGood: 2, defaultExc: 0.5 },
  { id: "revenueChurn", label: "Revenue Churn", category: "Unit Economics", unit: "%", desc: "% of MRR lost per month", inputKey: "revenueChurn", computed: false, higher: false, defaultGood: 2, defaultExc: 0.5 },
  { id: "acv", label: "ACV", category: "Unit Economics", unit: "₹", desc: "Average Contract Value annually", inputKey: "acv", computed: false, higher: true, defaultGood: 100000, defaultExc: 500000 },
  { id: "arpu", label: "ARPU", category: "Unit Economics", unit: "₹", desc: "Average Revenue Per User monthly", computed: true, higher: true, defaultGood: 5000, defaultExc: 20000 },
  // Survival
  { id: "runway", label: "Runway", category: "Survival", unit: "mo", desc: "Months of cash at current burn", computed: true, higher: true, defaultGood: 18, defaultExc: 24 },
  { id: "burn", label: "Monthly Burn", category: "Survival", unit: "₹", desc: "Cash spent per month", inputKey: "burn", computed: false, higher: false, defaultGood: 1000000, defaultExc: 500000 },
  { id: "cash", label: "Cash in Bank", category: "Survival", unit: "₹", desc: "Total cash available", inputKey: "cash", computed: false, higher: true, defaultGood: 10000000, defaultExc: 50000000 },
  { id: "defaultAlive", label: "Default Alive", category: "Survival", unit: "", desc: "Will the company reach profitability before cash runs out?", computed: true, higher: true, defaultGood: 0, defaultExc: 1 },
  { id: "workingCapital", label: "Working Capital", category: "Survival", unit: "₹", desc: "Current assets minus current liabilities", inputKey: "workingCapital", computed: false, higher: true, defaultGood: 1000000, defaultExc: 5000000 },
  // SaaS Specific
  { id: "dauMau", label: "DAU/MAU", category: "SaaS", unit: "%", desc: "Daily active users / Monthly active users", inputKey: "dauMau", computed: false, higher: true, defaultGood: 20, defaultExc: 50 },
  { id: "activationRate", label: "Activation Rate", category: "SaaS", unit: "%", desc: "% of signups who become active users", inputKey: "activationRate", computed: false, higher: true, defaultGood: 40, defaultExc: 70 },
  { id: "featureAdoption", label: "Feature Adoption", category: "SaaS", unit: "%", desc: "% of users using core feature", inputKey: "featureAdoption", computed: false, higher: true, defaultGood: 50, defaultExc: 80 },
  { id: "arrPerSeat", label: "ARR / Seat", category: "SaaS", unit: "₹", desc: "ARR divided by number of seats", inputKey: "arrPerSeat", computed: false, higher: true, defaultGood: 50000, defaultExc: 200000 },
  { id: "paybackPeriod", label: "Payback Period", category: "SaaS", unit: "mo", desc: "Months to recover customer acquisition cost", computed: true, higher: false, defaultGood: 18, defaultExc: 12 },
];

const BUNDLES_KEY = "binos_metric_bundles_v2";
const ACTIVE_BUNDLE_KEY = "binos_active_bundle_v2";

function getSavedBundles() {
  try { return JSON.parse(localStorage.getItem(BUNDLES_KEY) || "[]"); } catch { return []; }
}
function saveAllBundles(b) { try { localStorage.setItem(BUNDLES_KEY, JSON.stringify(b)); } catch {} }
function getActiveBundle() { try { return JSON.parse(localStorage.getItem(ACTIVE_BUNDLE_KEY) || "null"); } catch { return null; } }
function saveActiveBundle(b) { try { localStorage.setItem(ACTIVE_BUNDLE_KEY, JSON.stringify(b)); } catch {} }

function scoreMetric(value, good, excellent, higher = true) {
  if (higher) {
    if (value >= excellent) return 100;
    if (value <= 0) return 0;
    if (value >= good) return Math.round(60 + ((value - good) / Math.max(excellent - good, 1)) * 40);
    return Math.round((value / good) * 60);
  } else {
    if (value <= excellent) return 100;
    if (value >= good * 2) return 0;
    if (value <= good) return Math.round(60 + ((good - value) / Math.max(good - excellent, 1)) * 40);
    return Math.round(((good * 2 - value) / good) * 60);
  }
}

function getSignal(score) {
  if (score >= 80) return { label: "Strong", color: "#16a34a" };
  if (score >= 60) return { label: "Good", color: "#2563eb" };
  if (score >= 40) return { label: "Average", color: "#d97706" };
  if (score >= 20) return { label: "Weak", color: "#ea580c" };
  return { label: "Critical", color: "#dc2626" };
}

function fmt(n, symbol = "₹") {
  if (!n || isNaN(n) || !isFinite(n)) return "N/A";
  if (n >= 10000000) return `${symbol}${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `${symbol}${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${symbol}${(n / 1000).toFixed(0)}K`;
  return `${symbol}${n}`;
}

const CATEGORIES = ["Growth", "Efficiency", "Profitability", "Unit Economics", "Survival", "SaaS"];

// Bundle Builder Modal
function BundleBuilderModal({ onClose, onSave, existingBundle }) {
  const [selectedMetrics, setSelectedMetrics] = useState(
    existingBundle ? existingBundle.metrics : []
  );
  const [benchmarks, setBenchmarks] = useState(
    existingBundle ? existingBundle.benchmarks : {}
  );
  const [bundleName, setBundleName] = useState(existingBundle ? existingBundle.name : "");
  const [activeCategory, setActiveCategory] = useState("Growth");

  const toggleMetric = (metricId) => {
    if (selectedMetrics.includes(metricId)) {
      setSelectedMetrics(prev => prev.filter(m => m !== metricId));
    } else {
      setSelectedMetrics(prev => [...prev, metricId]);
      const m = METRICS_LIBRARY.find(m => m.id === metricId);
      if (m && !benchmarks[metricId]) {
        setBenchmarks(prev => ({ ...prev, [metricId]: { good: m.defaultGood, excellent: m.defaultExc } }));
      }
    }
  };

  const updateBenchmark = (metricId, field, value) => {
    setBenchmarks(prev => ({ ...prev, [metricId]: { ...prev[metricId], [field]: parseFloat(value) || 0 } }));
  };

  const handleSave = () => {
    if (!bundleName.trim()) { alert("Please name your bundle"); return; }
    if (selectedMetrics.length === 0) { alert("Please select at least one metric"); return; }
    onSave({ id: existingBundle?.id || Date.now(), name: bundleName, metrics: selectedMetrics, benchmarks });
    onClose();
  };

  const categoryMetrics = METRICS_LIBRARY.filter(m => m.category === activeCategory);
  const selectedInCategory = categoryMetrics.filter(m => selectedMetrics.includes(m.id));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 24 }}>
      <div style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, width: "100%", maxWidth: 860, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 900, color: "#fff", margin: "0 0 4px", letterSpacing: "-0.5px" }}>
              {existingBundle ? "Edit Bundle" : "Build Metric Bundle"}
            </h3>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", margin: 0 }}>Pick metrics and set your own benchmarks for each</p>
          </div>
          <button onClick={onClose} style={{ padding: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, cursor: "pointer", color: "rgba(255,255,255,0.4)" }}>
            <X style={{ width: 15, height: 15 }} />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", flex: 1, overflow: "hidden" }}>

          {/* Left — Metric picker */}
          <div style={{ borderRight: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Category tabs */}
            <div style={{ display: "flex", overflowX: "auto", padding: "12px 16px", gap: 6, borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  style={{ padding: "5px 12px", borderRadius: 100, fontSize: 11, fontWeight: 700, border: "none", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit", background: activeCategory === cat ? "#dc2626" : "rgba(255,255,255,0.05)", color: activeCategory === cat ? "#fff" : "rgba(255,255,255,0.4)", transition: "all 0.15s" }}>
                  {cat}
                  {selectedInCategory.length > 0 && activeCategory !== cat && ` (${METRICS_LIBRARY.filter(m => m.category === cat && selectedMetrics.includes(m.id)).length})`}
                </button>
              ))}
            </div>

            {/* Metrics list */}
            <div style={{ overflowY: "auto", flex: 1, padding: "8px 16px" }}>
              {categoryMetrics.map(m => {
                const isSelected = selectedMetrics.includes(m.id);
                return (
                  <div key={m.id} onClick={() => toggleMetric(m.id)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, cursor: "pointer", transition: "all 0.15s", marginBottom: 4, background: isSelected ? "rgba(220,38,38,0.08)" : "transparent", border: isSelected ? "1px solid rgba(220,38,38,0.2)" : "1px solid transparent" }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ width: 18, height: 18, borderRadius: 5, border: isSelected ? "none" : "1.5px solid rgba(255,255,255,0.15)", background: isSelected ? "#dc2626" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                      {isSelected && <div style={{ width: 8, height: 8, background: "#fff", borderRadius: 2 }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#fff" : "rgba(255,255,255,0.6)", margin: "0 0 2px" }}>{m.label}</p>
                      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", margin: 0, fontWeight: 400 }}>{m.desc}</p>
                    </div>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", fontWeight: 600 }}>{m.unit}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right — Benchmark setter for selected metrics */}
          <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>
                {selectedMetrics.length} metrics selected — set benchmarks
              </p>
            </div>

            <div style={{ overflowY: "auto", flex: 1, padding: "8px 16px" }}>
              {selectedMetrics.length === 0 && (
                <div style={{ padding: "40px 24px", textAlign: "center" }}>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.15)", fontWeight: 500 }}>Select metrics from the left to set benchmarks</p>
                </div>
              )}
              {selectedMetrics.map(metricId => {
                const m = METRICS_LIBRARY.find(m => m.id === metricId);
                if (!m) return null;
                const bench = benchmarks[metricId] || { good: m.defaultGood, excellent: m.defaultExc };
                return (
                  <div key={metricId} style={{ padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{m.label}</span>
                        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginLeft: 8 }}>{m.category}</span>
                      </div>
                      <button onClick={() => toggleMetric(metricId)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.2)", padding: 4 }}
                        onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                        onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.2)"}>
                        <X style={{ width: 12, height: 12 }} />
                      </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <div>
                        <p style={{ fontSize: 9, fontWeight: 700, color: "#d97706", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 4px" }}>
                          {m.higher ? "Good above" : "Good below"} ({m.unit})
                        </p>
                        <input type="number" value={bench.good} step="any"
                          onChange={e => updateBenchmark(metricId, "good", e.target.value)}
                          style={{ width: "100%", padding: "7px 10px", background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#d97706", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <p style={{ fontSize: 9, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 4px" }}>
                          {m.higher ? "Excellent above" : "Excellent below"} ({m.unit})
                        </p>
                        <input type="number" value={bench.excellent} step="any"
                          onChange={e => updateBenchmark(metricId, "excellent", e.target.value)}
                          style={{ width: "100%", padding: "7px 10px", background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#16a34a", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Save */}
            <div style={{ padding: "16px", borderTop: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
              <input value={bundleName} onChange={e => setBundleName(e.target.value)} placeholder="Name your bundle e.g. My Pre-Seed India Bundle"
                style={{ width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, outline: "none", fontSize: 13, fontWeight: 500, color: "#fff", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10 }}
                onFocus={e => e.target.style.borderColor = "#dc2626"}
                onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
              <button onClick={handleSave}
                style={{ width: "100%", padding: "11px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                <Save style={{ width: 14, height: 14 }} /> Save Bundle
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const TOOLTIPS = {
  mrr: "Monthly Recurring Revenue", growth: "Month-over-month revenue growth rate",
  burn: "Cash spent per month", cash: "Total cash in bank",
  cac: "Cost to acquire one customer", ltv: "Lifetime value of a customer",
  churn: "% of customers lost each month", grossMargin: "Revenue minus COGS as %",
  employees: "Total full-time employees", customers: "Number of paying customers", nrr: "Net Revenue Retention",
  salesSpend: "Monthly sales & marketing spend", grr: "Gross Revenue Retention",
  revenueChurn: "% of MRR lost per month", expansionRevenue: "% revenue from expansion",
  ebitdaMargin: "EBITDA as % of revenue", acv: "Average Contract Value",
  dauMau: "Daily / Monthly active users ratio", activationRate: "% signups who activate",
  featureAdoption: "% users using core feature", arrPerSeat: "ARR per seat",
  operatingLeverage: "Revenue growth / opex growth", contributionMargin: "Revenue minus variable costs",
  workingCapital: "Current assets minus liabilities", rdSpend: "R&D spend monthly",
  revenueChurnInput: "Monthly revenue churn %",
};

function InputField({ label, value, onChange, prefix = "", suffix = "", tooltip }) {
  const [showTip, setShowTip] = useState(false);
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>{label}</span>
        <div style={{ position: "relative" }}>
          <Info style={{ width: 10, height: 10, color: "rgba(255,255,255,0.15)", cursor: "pointer" }}
            onMouseEnter={() => setShowTip(true)} onMouseLeave={() => setShowTip(false)} />
          {showTip && tooltip && (
            <div style={{ position: "absolute", left: 14, top: 0, zIndex: 50, width: 180, background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)", fontSize: 11, padding: 10, borderRadius: 8, fontWeight: 500, lineHeight: 1.6, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", whiteSpace: "normal" }}>{tooltip}</div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {prefix && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontWeight: 600 }}>{prefix}</span>}
        <input type="number" value={value} onChange={e => onChange(parseFloat(e.target.value) || 0)}
          style={{ width: 90, padding: "5px 8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 7, outline: "none", fontWeight: 700, color: "#fff", fontSize: 12, fontFamily: "inherit", textAlign: "right", transition: "border-color 0.2s" }}
          onFocus={e => e.target.style.borderColor = "#dc2626"}
          onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
        {suffix && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontWeight: 600, minWidth: 16 }}>{suffix}</span>}
      </div>
    </div>
  );
}

export default function FinancialCalculator() {
  const [inputs, setInputs] = useState({
    mrr: 100000, growth: 12, burn: 80000, cash: 1500000,
    cac: 1200, ltv: 4800, churn: 2.5, grossMargin: 70,
    employees: 12, customers: 50, nrr: 105, salesSpend: 30000, grr: 90,
    revenueChurn: 1.5, expansionRevenue: 15, ebitdaMargin: -10,
    acv: 50000, dauMau: 30, activationRate: 45, featureAdoption: 60,
    arrPerSeat: 100000, operatingLeverage: 1.2, contributionMargin: 55,
    workingCapital: 500000, rdSpend: 20000,
  });

  const [currency, setCurrency] = useState(CURRENCIES[0]);
  const [bundles, setBundles] = useState(getSavedBundles());
  const [activeBundle, setActiveBundle] = useState(getActiveBundle());
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);

  const set = key => val => setInputs(p => ({ ...p, [key]: val }));
  const sym = currency.symbol;

  // Computed values
  const arr = inputs.mrr * 12;
  const runway = inputs.burn > 0 ? +(inputs.cash / inputs.burn).toFixed(1) : 999;
  const burnMultiple = inputs.mrr * (inputs.growth / 100) > 0 ? +(inputs.burn / (inputs.mrr * (inputs.growth / 100))).toFixed(2) : 999;
  const ltvCac = inputs.cac > 0 ? +(inputs.ltv / inputs.cac).toFixed(2) : 0;
  const yoyGrowth = +(((1 + inputs.growth/100) ** 12 - 1) * 100).toFixed(1);
  const ruleOf40 = +(yoyGrowth + inputs.grossMargin - 100).toFixed(1);
  const magicNumber = inputs.salesSpend > 0 ? +((inputs.mrr * (inputs.growth / 100) * 12) / inputs.salesSpend).toFixed(2) : 0;
  const arrPerEmployee = inputs.employees > 0 ? Math.round(arr / inputs.employees) : 0;
  const revenuePerEmployee = inputs.employees > 0 ? Math.round(inputs.mrr / inputs.employees) : 0;
  const quickRatio = inputs.churn > 0 ? +(inputs.growth / inputs.churn).toFixed(2) : 0;
  const grossProfit = Math.round(inputs.mrr * (inputs.grossMargin / 100));
  const smRatio = inputs.mrr > 0 ? +((inputs.salesSpend / inputs.mrr) * 100).toFixed(1) : 0;
  const opexRatio = inputs.mrr > 0 ? +((inputs.burn / inputs.mrr) * 100).toFixed(1) : 0;
  const rdRatio = inputs.mrr > 0 ? +((inputs.rdSpend / inputs.mrr) * 100).toFixed(1) : 0;
  const cacPayback = inputs.grossMargin > 0 && inputs.mrr > 0 ? +(inputs.cac / (inputs.mrr / Math.max(inputs.employees, 1) * (inputs.grossMargin / 100))).toFixed(1) : 0;
  const arpu = inputs.customers > 0 ? Math.round(inputs.mrr / inputs.customers) : 0;
  const paybackPeriod = inputs.grossMargin > 0 ? +(inputs.cac / ((inputs.mrr / Math.max(inputs.employees, 1)) * (inputs.grossMargin / 100))).toFixed(1) : 0;
  const defaultAlive = (inputs.growth / 100 * inputs.mrr) > inputs.burn ? 1 : 0;

  const COMPUTED_VALUES = {
    mrr: inputs.mrr, arr, growth: inputs.growth, nrr: inputs.nrr, grr: inputs.grr,
    expansionRevenue: inputs.expansionRevenue, burnMultiple, magicNumber, arrPerEmployee,
    revenuePerEmployee, smRatio, rdRatio: rdRatio, opexRatio, quickRatio, grossMargin: inputs.grossMargin,
    ruleOf40, ebitdaMargin: inputs.ebitdaMargin, operatingLeverage: inputs.operatingLeverage,
    grossProfit, contributionMargin: inputs.contributionMargin, ltv: inputs.ltv, cac: inputs.cac,
    ltvCac, cacPayback, churn: inputs.churn, revenueChurn: inputs.revenueChurn,
    acv: inputs.acv, arpu, runway, burn: inputs.burn, cash: inputs.cash,
    defaultAlive, workingCapital: inputs.workingCapital, dauMau: inputs.dauMau,
    activationRate: inputs.activationRate, featureAdoption: inputs.featureAdoption,
    arrPerSeat: inputs.arrPerSeat, paybackPeriod, yoyGrowth,
  };

  // Score all selected metrics
  const getMetricScore = (metricId) => {
    const m = METRICS_LIBRARY.find(m => m.id === metricId);
    if (!m) return 0;
    const value = COMPUTED_VALUES[metricId];
    if (value === undefined || value === null) return 0;
    const bench = activeBundle?.benchmarks?.[metricId] || { good: m.defaultGood, excellent: m.defaultExc };
    return scoreMetric(value, bench.good, bench.excellent, m.higher);
  };

  const activeMetrics = activeBundle?.metrics || ["burnMultiple", "ruleOf40", "nrr", "grossMargin", "runway", "ltvCac", "magicNumber", "quickRatio", "growth", "arrPerEmployee"];
  const scores = Object.fromEntries(activeMetrics.map(id => [id, getMetricScore(id)]));
  const overallScore = activeMetrics.length > 0 ? Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / activeMetrics.length) : 0;
  const verdict = overallScore >= 70 ? { text: "Invest", color: "#16a34a" } : overallScore >= 50 ? { text: "Watch", color: "#d97706" } : { text: "Pass", color: "#dc2626" };

  const radarData = activeMetrics.slice(0, 8).map(id => {
    const m = METRICS_LIBRARY.find(m => m.id === id);
    return { metric: m?.label?.split(" ")[0] || id, score: scores[id] || 0 };
  });

  const saveBundle = (bundle) => {
    const updated = [...bundles.filter(b => b.id !== bundle.id), bundle];
    setBundles(updated);
    saveAllBundles(updated);
    setActiveBundle(bundle);
    saveActiveBundle(bundle);
  };

  const deleteBundle = (id) => {
    const updated = bundles.filter(b => b.id !== id);
    setBundles(updated);
    saveAllBundles(updated);
    if (activeBundle?.id === id) { setActiveBundle(null); saveActiveBundle(null); }
  };

  const INPUT_SECTIONS = [
    { title: "Core Revenue", fields: [
      { label: "Monthly Recurring Revenue", key: "mrr", prefix: sym, tooltip: TOOLTIPS.mrr },
      { label: "MoM Growth Rate", key: "growth", suffix: "%", tooltip: TOOLTIPS.growth },
      { label: "Net Revenue Retention", key: "nrr", suffix: "%", tooltip: TOOLTIPS.nrr },
      { label: "Gross Revenue Retention", key: "grr", suffix: "%", tooltip: TOOLTIPS.grr },
      { label: "Gross Margin", key: "grossMargin", suffix: "%", tooltip: TOOLTIPS.grossMargin },
      { label: "Expansion Revenue", key: "expansionRevenue", suffix: "%", tooltip: TOOLTIPS.expansionRevenue },
    ]},
    { title: "Cost & Efficiency", fields: [
      { label: "Monthly Burn", key: "burn", prefix: sym, tooltip: TOOLTIPS.burn },
      { label: "Sales & Marketing Spend", key: "salesSpend", prefix: sym, tooltip: TOOLTIPS.salesSpend },
      { label: "R&D Spend", key: "rdSpend", prefix: sym, tooltip: TOOLTIPS.rdSpend },
      { label: "Employees", key: "employees", tooltip: TOOLTIPS.employees },
      { label: "Customers", key: "customers", tooltip: TOOLTIPS.customers },
      { label: "Cash in Bank", key: "cash", prefix: sym, tooltip: TOOLTIPS.cash },
    ]},
    { title: "Unit Economics", fields: [
      { label: "Customer Acquisition Cost", key: "cac", prefix: sym, tooltip: TOOLTIPS.cac },
      { label: "Customer Lifetime Value", key: "ltv", prefix: sym, tooltip: TOOLTIPS.ltv },
      { label: "Logo Churn Rate", key: "churn", suffix: "%", tooltip: TOOLTIPS.churn },
      { label: "Revenue Churn Rate", key: "revenueChurn", suffix: "%", tooltip: TOOLTIPS.revenueChurn },
      { label: "Average Contract Value", key: "acv", prefix: sym, tooltip: TOOLTIPS.acv },
    ]},
    { title: "Product & SaaS", fields: [
      { label: "DAU/MAU Ratio", key: "dauMau", suffix: "%", tooltip: TOOLTIPS.dauMau },
      { label: "Activation Rate", key: "activationRate", suffix: "%", tooltip: TOOLTIPS.activationRate },
      { label: "Feature Adoption Rate", key: "featureAdoption", suffix: "%", tooltip: TOOLTIPS.featureAdoption },
      { label: "ARR Per Seat", key: "arrPerSeat", prefix: sym, tooltip: TOOLTIPS.arrPerSeat },
    ]},
    { title: "Other", fields: [
      { label: "EBITDA Margin", key: "ebitdaMargin", suffix: "%", tooltip: TOOLTIPS.ebitdaMargin },
      { label: "Operating Leverage", key: "operatingLeverage", suffix: "x", tooltip: TOOLTIPS.operatingLeverage },
      { label: "Contribution Margin", key: "contributionMargin", suffix: "%", tooltip: TOOLTIPS.contributionMargin },
      { label: "Working Capital", key: "workingCapital", prefix: sym, tooltip: TOOLTIPS.workingCapital },
    ]},
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: "#fff", margin: "0 0 3px", letterSpacing: "-0.5px" }}>VC Metrics Terminal</h2>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", margin: 0 }}>
            {activeBundle ? `Bundle: ${activeBundle.name} · ${activeBundle.metrics.length} metrics` : "No bundle active — showing default metrics"}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <select value={currency.code} onChange={e => setCurrency(CURRENCIES.find(c => c.code === e.target.value))}
            style={{ padding: "7px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontWeight: 600, fontSize: 12, color: "#fff", outline: "none", cursor: "pointer", fontFamily: "inherit" }}>
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 18px", borderRadius: 8, background: verdict.color + "15", border: `1px solid ${verdict.color}30` }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: verdict.color }} />
            <span style={{ fontWeight: 800, fontSize: 13, color: verdict.color }}>{verdict.text}</span>
            <span style={{ fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: "-1px", marginLeft: 6 }}>{overallScore}</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>/100</span>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 32 }}>

        {/* LEFT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
<ExcelImport
            onImport={(values) => setInputs(prev => ({ ...prev, ...values }))}
            onDownloadTemplate={() => window.open('https://binos-ai.com/Binos_Metrics_Template.xlsx')}
/>
          {/* Bundle selector */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <p style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.15em", margin: 0 }}>Metric Bundles</p>
              <button onClick={() => { setEditingBundle(null); setShowBuilder(true); }}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 7, fontSize: 11, fontWeight: 700, color: "#dc2626", cursor: "pointer", fontFamily: "inherit" }}>
                <Plus style={{ width: 11, height: 11 }} /> New Bundle
              </button>
            </div>

            {bundles.length === 0 && (
              <div style={{ padding: "16px", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 10, textAlign: "center" }}>
                <p style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", margin: 0, fontWeight: 500 }}>No bundles yet. Create one to get started.</p>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {bundles.map(bundle => (
                <div key={bundle.id}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, border: activeBundle?.id === bundle.id ? "1px solid rgba(220,38,38,0.4)" : "1px solid rgba(255,255,255,0.06)", background: activeBundle?.id === bundle.id ? "rgba(220,38,38,0.08)" : "#1a1a1a", cursor: "pointer", transition: "all 0.15s" }}
                  onClick={() => { setActiveBundle(bundle); saveActiveBundle(bundle); }}>
                  <Package style={{ width: 14, height: 14, color: activeBundle?.id === bundle.id ? "#dc2626" : "rgba(255,255,255,0.3)", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: activeBundle?.id === bundle.id ? "#fff" : "rgba(255,255,255,0.6)", margin: "0 0 1px" }}>{bundle.name}</p>
                    <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: 0, fontWeight: 500 }}>{bundle.metrics.length} metrics</p>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={e => { e.stopPropagation(); setEditingBundle(bundle); setShowBuilder(true); }}
                      style={{ padding: 5, background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.2)", borderRadius: 5 }}
                      onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                      onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.2)"}>
                      <Info style={{ width: 12, height: 12 }} />
                    </button>
                    <button onClick={e => { e.stopPropagation(); deleteBundle(bundle.id); }}
                      style={{ padding: 5, background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.2)", borderRadius: 5 }}
                      onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                      onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.2)"}>
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Inputs */}
          {INPUT_SECTIONS.map((section, si) => (
            <div key={si}>
              <p style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "0.15em", margin: "0 0 2px" }}>{section.title}</p>
              {section.fields.map(f => (
                <InputField key={f.key} label={f.label} value={inputs[f.key]} onChange={set(f.key)}
                  prefix={f.prefix} suffix={f.suffix} tooltip={f.tooltip} />
              ))}
            </div>
          ))}
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Radar */}
          <div style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: 20 }}>
            <p style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "0.15em", margin: "0 0 12px" }}>Signal Radar</p>
            <ResponsiveContainer width="100%" height={180}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.05)" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fontWeight: 600, fill: "rgba(255,255,255,0.3)" }} />
                <Radar dataKey="score" stroke="#dc2626" fill="#dc2626" fillOpacity={0.08} strokeWidth={1.5} />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Metrics table */}
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 140px", gap: 12, padding: "6px 0 10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Metric</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "0.1em", textAlign: "right" }}>Value</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Signal</span>
            </div>

            {activeMetrics.map(metricId => {
              const m = METRICS_LIBRARY.find(m => m.id === metricId);
              if (!m) return null;
              const rawValue = COMPUTED_VALUES[metricId];
              const score = scores[metricId] || 0;
              const sig = getSignal(score);
              const bench = activeBundle?.benchmarks?.[metricId] || { good: m.defaultGood, excellent: m.defaultExc };

              let displayValue = "N/A";
              if (rawValue !== undefined && !isNaN(rawValue) && isFinite(rawValue)) {
                if (m.unit === "₹") displayValue = fmt(rawValue, currency.symbol);
                else if (m.unit === "") displayValue = rawValue === 1 ? "Yes" : rawValue === 0 ? "No" : rawValue;
                else displayValue = `${typeof rawValue === 'number' ? rawValue.toFixed(rawValue % 1 === 0 ? 0 : 1) : rawValue}${m.unit}`;
              }

              return (
                <div key={metricId} style={{ display: "grid", gridTemplateColumns: "1fr 80px 140px", gap: 12, alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <div>
                    <span style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>{m.label}</span>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", marginLeft: 8 }}>
                      {m.higher ? `>${bench.good}${m.unit}` : `<${bench.good}${m.unit}`}
                    </span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#fff", letterSpacing: "-0.2px", textAlign: "right" }}>{displayValue}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 100, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${score}%`, background: sig.color, borderRadius: 100, transition: "width 0.6s ease" }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: sig.color, minWidth: 48, textAlign: "right" }}>{sig.label}</span>
                  </div>
                </div>
              );
            })}

            {/* Overall */}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 20 }}>
                {[
                  { label: "Rule of 40", value: `${ruleOf40}%`, good: ruleOf40 >= 40 },
                  { label: "Burn Multiple", value: `${burnMultiple}x`, good: burnMultiple <= 2 },
                  { label: "Magic Number", value: `${magicNumber}x`, good: magicNumber >= 1 },
                  { label: "Quick Ratio", value: `${quickRatio}x`, good: quickRatio >= 4 },
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
                    {s.good ? <TrendingUp style={{ width: 12, height: 12, color: "#16a34a", marginTop: 3 }} /> : <TrendingDown style={{ width: 12, height: 12, color: "#dc2626", marginTop: 3 }} />}
                    <div>
                      <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: 0 }}>{s.label}</p>
                      <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0 }}>{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: "0 0 2px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Overall Signal</p>
                <p style={{ fontSize: 20, fontWeight: 900, color: verdict.color, margin: 0, letterSpacing: "-0.5px" }}>{verdict.text} · {overallScore}/100</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bundle Builder Modal */}
      {showBuilder && (
        <BundleBuilderModal
          onClose={() => { setShowBuilder(false); setEditingBundle(null); }}
          onSave={saveBundle}
          existingBundle={editingBundle}
        />
      )}
    </div>
  );
}
